import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Linkedin, Twitter, MessageCircle, Link2, Check } from "lucide-react";
import { fetchBilancio } from "../api";
import TrasparenzaCard from "../components/TrasparenzaCard";

const SEM = {
  verde: { emoji: "🟢", label: "Salute solida", color: "#2e7d32", soft: "#e8f5e9", border: "#cfe8d1" },
  giallo: { emoji: "🟡", label: "Attenzione", color: "#b7791f", soft: "#fff8e1", border: "#f3d78a" },
  rosso: { emoji: "🔴", label: "Situazione critica", color: "#d32f2f", soft: "#fdecea", border: "#f6cfcb" },
};

const nf = new Intl.NumberFormat("it-IT");
const fmt = (v) => (typeof v === "number" ? nf.format(v) : v);

const Bilancio60Detail = () => {
  const { ticker } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchBilancio(ticker)
      .then(setB)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [ticker]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-gray-400">Caricamento...</div>;

  if (notFound || !b) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-serif-display text-3xl text-sq-navy">Scheda non trovata</h1>
        <Link to="/bilancio-60-secondi" className="text-sq-orange mt-4 inline-block">Torna all'indice</Link>
      </div>
    );
  }

  const sem = SEM[b.semaforo_salute] || SEM.giallo;
  const ric = b.ricavi || {};
  const ricUp = (ric.variazione_percentuale ?? 0) >= 0;
  const maxRic = Math.max(ric.anno_corrente || 0, ric.anno_precedente || 0) || 1;
  const wCurr = Math.round(((ric.anno_corrente || 0) / maxRic) * 100);
  const wPrev = Math.round(((ric.anno_precedente || 0) / maxRic) * 100);
  const ebitda = b.ebitda || {};
  const ebitNeg = (ebitda.valore ?? 0) < 0;
  const utile = b.utile_netto || {};
  const utileImproved = (utile.variazione_percentuale ?? 0) >= 0;
  const pfn = b.posizione_finanziaria_netta || {};

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 fade-in">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-sq-orange">Home</Link>
        <span>/</span>
        <Link to="/bilancio-60-secondi" className="hover:text-sq-orange">Bilancio in 60 secondi</Link>
        <span>/</span>
        <span className="text-sq-navy font-medium">{b.ticker}</span>
      </div>

      <article className="bg-white border border-sq rounded-lg overflow-hidden shadow-sm">
        {/* Header */}
        <header className="bg-sq-navy text-white px-6 md:px-8 py-6">
          <div className="text-[12px] uppercase tracking-[0.14em] font-bold" style={{ color: "#ffd28a" }}>
            ⏱️ Bilancio in 60 secondi
          </div>
          <h1 className="font-serif-display text-3xl md:text-4xl mt-2">{b.societa}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="bg-sq-orange text-white text-[11px] uppercase font-bold px-2.5 py-1 rounded">EGM</span>
            <span className="text-xs bg-white/12 border border-white/20 px-2.5 py-1 rounded-full font-semibold">Ticker: {b.ticker}</span>
            <span className="text-xs bg-white/12 border border-white/20 px-2.5 py-1 rounded-full font-semibold">Bilancio {b.anno_bilancio}</span>
          </div>
          <p className="text-sm mt-3" style={{ color: "#cbd6e6" }}>
            {b.settore} · {b.mercato}{b.data_quotazione_egm ? ` · Quotata dal ${b.data_quotazione_egm}` : ""}
          </p>
        </header>

        <div className="px-6 md:px-8 py-6">
          {/* Ricavi horizontal bars */}
          <SectionTitle>Ricavi (migliaia di €)</SectionTitle>
          <div className="space-y-4">
            <HBar label={`${(parseInt(b.anno_bilancio) - 1) || "Anno prec."}`} width={wPrev} value={fmt(ric.anno_precedente)} color="#c7cede" textDark />
            <HBar label={`${b.anno_bilancio}`} width={wCurr} value={fmt(ric.anno_corrente)} color={ricUp ? "#2e7d32" : "#d32f2f"} />
          </div>
          <div className="mt-4">
            <span
              className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-full"
              style={{ background: ricUp ? "#e8f5e9" : "#fdecea", color: ricUp ? "#2e7d32" : "#d32f2f" }}
            >
              {ricUp ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
              {ricUp ? "↑" : "↓"} {ric.variazione_percentuale}% Ricavi {ricUp ? "in crescita" : "in calo"}
            </span>
          </div>
          {ric.nota && <p className="text-xs text-gray-500 mt-2 leading-relaxed">{ric.nota}</p>}

          {/* KPIs */}
          <SectionTitle>Indicatori chiave</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border border-sq rounded-lg p-4 bg-[#fafbfc]">
              <div className="text-xs text-gray-500 font-semibold">EBITDA</div>
              <div className="text-2xl font-extrabold" style={{ color: ebitNeg ? "#d32f2f" : "#2e7d32" }}>
                {ebitda.valore < 0 ? "−" : ""}{fmt(Math.abs(ebitda.valore ?? 0))}
              </div>
              <div className="text-sm font-bold mt-1" style={{ color: (ebitda.margin_percentuale ?? 0) < 0 ? "#d32f2f" : "#2e7d32" }}>
                Margine {ebitda.margin_percentuale}%
              </div>
              {ebitda.margin_adjusted != null && (
                <span className="inline-block text-xs font-semibold mt-1.5 bg-[#e8f5e9] text-[#2e7d32] px-2 py-0.5 rounded">
                  Adj. {ebitda.margin_adjusted}%
                </span>
              )}
              {ebitda.nota && <div className="text-xs text-gray-500 mt-1.5 leading-relaxed">{ebitda.nota}</div>}
            </div>
            <div className="border border-sq rounded-lg p-4 bg-[#fafbfc]">
              <div className="text-xs text-gray-500 font-semibold">Utile netto</div>
              <div className="text-2xl font-extrabold" style={{ color: utile.positivo ? "#2e7d32" : "#d32f2f" }}>
                {(utile.valore ?? 0) < 0 ? "−" : ""}{fmt(Math.abs(utile.valore ?? 0))}
              </div>
              <div className="text-sm font-bold mt-1 flex items-center gap-1" style={{ color: utileImproved ? "#b7791f" : "#d32f2f" }}>
                {utileImproved ? "↑" : "↓"} {Math.abs(utile.variazione_percentuale ?? 0)}% {utileImproved ? "(in miglioramento)" : ""}
              </div>
              {utile.nota && <div className="text-xs text-gray-500 mt-1.5 leading-relaxed">{utile.nota}</div>}
            </div>
            <div className="border border-sq rounded-lg p-4 bg-[#fafbfc] sm:col-span-2">
              <div className="text-xs text-gray-500 font-semibold">Posizione Finanziaria Netta (€/000)</div>
              <div className="text-2xl font-extrabold" style={{ color: (pfn.valore ?? 0) < 0 ? "#b7791f" : "#2e7d32" }}>
                {(pfn.valore ?? 0) < 0 ? "−" : ""}{fmt(Math.abs(pfn.valore ?? 0))}
              </div>
              {pfn.giudizio && <div className="text-xs text-gray-500 mt-1 capitalize">Giudizio: {pfn.giudizio}</div>}
              {pfn.nota && <div className="text-xs text-gray-500 mt-1">{pfn.nota}</div>}
            </div>
          </div>

          {/* Health */}
          <SectionTitle>Salute finanziaria</SectionTitle>
          <div
            className="flex items-center gap-4 rounded-lg p-4"
            style={{ background: sem.soft, border: `1px solid ${sem.border}` }}
          >
            <span className="text-4xl leading-none">{sem.emoji}</span>
            <div>
              <div className="font-extrabold" style={{ color: sem.color }}>{sem.label}</div>
              <div className="text-sm text-gray-700 mt-0.5">{b.motivazione_semaforo}</div>
            </div>
          </div>

          {/* Extra KPI block (e.g. Backlog) — rendered only when present */}
          {b.kpi_extra && (
            <div
              className="mt-6 rounded-xl px-6 py-5 text-white"
              style={{ background: "#122a4d", border: "1px solid #1f4680" }}
            >
              <div className="text-[12px] uppercase tracking-[0.1em] font-extrabold mb-1" style={{ color: "#ffd28a" }}>
                {b.kpi_extra.label}
              </div>
              <div className="text-2xl md:text-3xl font-extrabold">{b.kpi_extra.valore}</div>
              {b.kpi_extra.dettaglio && (
                <p className="text-sm mt-2 leading-relaxed" style={{ color: "#cbd6e6" }}>{b.kpi_extra.dettaglio}</p>
              )}
            </div>
          )}

          {/* Strengths */}
          <SectionTitle>Punti di forza</SectionTitle>
          <ul className="space-y-2">
            {(b.punti_di_forza || []).map((p, i) => (
              <li key={i} className="flex gap-3 items-start text-sm border rounded-lg px-3 py-2.5" style={{ background: "#e8f5e9", borderColor: "#cfe8d1" }}>
                <span>✅</span><span>{p}</span>
              </li>
            ))}
          </ul>

          {/* Risks */}
          <SectionTitle>Rischi</SectionTitle>
          <ul className="space-y-2">
            {(b.rischi || []).map((r, i) => (
              <li key={i} className="flex gap-3 items-start text-sm border rounded-lg px-3 py-2.5" style={{ background: "#fff6ec", borderColor: "#f7dcbd" }}>
                <span>⚠️</span><span>{r}</span>
              </li>
            ))}
          </ul>

          {/* Editorial */}
          <SectionTitle>Giudizio della redazione</SectionTitle>
          <div className="bg-sq-navy text-white rounded-xl p-6">
            <div className="text-[12px] uppercase tracking-[0.1em] font-extrabold mb-2" style={{ color: "#ffd28a" }}>
              Il punto di Società Quotate
            </div>
            <p className="text-[15px] leading-relaxed" style={{ color: "#eaf0f8" }}>{b.giudizio_redazionale}</p>
          </div>

          {/* Yellow banner */}
          {b.nota_speciale && (
            <div
              className="mt-5 rounded-lg px-4 py-3 text-sm flex gap-2.5 items-start"
              style={{ background: "#fff8e1", border: "1px solid #f3d78a", borderLeft: "5px solid #f5a623", color: "#6b4e00" }}
            >
              <span>📌</span>
              <span><strong>Nota speciale:</strong> {b.nota_speciale}</span>
            </div>
          )}

          {/* Transparency index card */}
          <TrasparenzaCard trasparenza={b.trasparenza} />

          {/* Share */}
          <ShareBar societa={b.societa} ticker={b.ticker} />
        </div>

        <footer className="px-6 md:px-8 py-4 border-t border-sq text-xs text-gray-500 text-center">
          {b.data_aggiornamento ? `Aggiornato il ${b.data_aggiornamento} · ` : ""}
          Elaborazione societaquotate.it su dati bilancio ufficiale · dati in migliaia di euro. Non costituisce consulenza finanziaria.
        </footer>
      </article>

      <div className="mt-6">
        <Link to="/bilancio-60-secondi" className="text-sm text-sq-orange hover:underline">← Tutte le schede EGM</Link>
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-3 text-[13px] uppercase tracking-wider text-gray-500 font-bold mt-8 mb-4">
    {children}
    <span className="flex-1 h-px bg-[#e6e8ec]" />
  </div>
);

const ShareBar = ({ societa, ticker }) => {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = `Bilancio in 60 secondi — ${societa} (${ticker}) su Società Quotate`;
  const enc = encodeURIComponent;
  const links = [
    { key: "linkedin", label: "LinkedIn", Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`, color: "#0a66c2" },
    { key: "x", label: "X", Icon: Twitter, href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`, color: "#111827" },
    { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle, href: `https://wa.me/?text=${enc(text + " " + url)}`, color: "#25d366" },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="mt-8 border-t border-sq pt-6" data-testid="share-bar">
      <div className="text-[13px] uppercase tracking-wider text-gray-500 font-bold mb-3">Condividi scheda</div>
      <div className="flex flex-wrap items-center gap-2.5">
        {links.map(({ key, label, Icon, href, color }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`share-${key}`}
            className="inline-flex items-center gap-2 text-sm font-semibold text-white px-3.5 py-2 rounded-full transition-opacity hover:opacity-90"
            style={{ background: color }}
          >
            <Icon size={16} /> {label}
          </a>
        ))}
        <button
          type="button"
          onClick={copy}
          data-testid="share-copy"
          className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-full border border-sq text-sq-navy bg-white hover:bg-sq-light transition-colors"
        >
          {copied ? <Check size={16} className="text-emerald-600" /> : <Link2 size={16} />}
          {copied ? "Copiato!" : "Copia link"}
        </button>
      </div>
    </div>
  );
};

const HBar = ({ label, width, value, color, textDark }) => (
  <div>
    <div className="flex justify-between text-xs text-gray-500 font-semibold mb-1">
      <span>{label}</span>
      <span style={{ color: textDark ? "#5b6785" : color }}>{value}</span>
    </div>
    <div className="w-full bg-gray-100 rounded-sm h-7 overflow-hidden">
      <div
        className="h-full rounded-sm transition-all duration-500"
        style={{ width: `${Math.max(width, 3)}%`, background: color }}
      />
    </div>
  </div>
);

export default Bilancio60Detail;
